import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Helper hook for breakpoints
function useWindowSize() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

const InstitutionalPartnerships = () => {
  const width = useWindowSize();

  // device breakpoints
  const isMobile = width < 600;
  const isTablet = width >= 600 && width < 1000;
  const isDesktop = width >= 1000;

  // State for form
  const [formData, setFormData] = useState({
    institutionName: '',
    contactPerson: '',
    email: '',
    phoneNumber: '',
    institutionType: '',
    partnershipInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // STYLES with RESPONSIVE adjustments
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      color: '#1a1a1a',
      position: 'relative',
      overflow: 'hidden'
    },
    heroSection: {
      padding: isMobile ? '70px 8px 35px' : (isTablet ? '90px 16px 60px' : '100px 20px 80px'),
      textAlign: 'center',
      position: 'relative',
      zIndex: 2,
      background: `
        linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.8) 100%),
        url('data:image/svg+xml,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600">
            <defs>
              <pattern id="circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <g fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1">
                  <circle cx="50" cy="50" r="3"/>
                  <path d="M0,50 Q25,25 50,50 T100,50"/>
                  <path d="M50,0 Q75,25 50,50 Q25,75 50,100"/>
                </g>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#circuit)"/>
            <g fill="rgba(255,255,255,0.05)">
              <circle cx="100" cy="100" r="50"/>
              <circle cx="300" cy="200" r="30"/>
              <circle cx="500" cy="150" r="40"/>
              <circle cx="700" cy="250" r="35"/>
              <circle cx="900" cy="100" r="45"/>
              <circle cx="1100" cy="200" r="25"/>
            </g>
            <g fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="2">
              <path d="M100,100 L300,200 L500,150 L700,250 L900,100 L1100,200"/>
            </g>
          </svg>
        `)}')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: 'white'
    },
    heroContent: {
      maxWidth: isMobile ? '95vw' : '800px',
      margin: '0 auto'
    },
    mainTitle: {
      fontSize: isMobile ? '2.2rem' : (isTablet ? '3rem' : '4rem'),
      fontWeight: 800,
      lineHeight: 1.13,
      marginBottom: isMobile ? '18px' : '24px',
      color: 'white',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
    },
    titleHighlight: {
      background: 'linear-gradient(135deg, #fff 0%, #f0f9ff 50%, #e0f2fe 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    heroDescription: {
      fontSize: isMobile ? '1rem' : '1.3rem',
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: 1.5,
      marginBottom: isMobile ? '24px' : '40px',
      textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
    },
    startButton: {
      background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
      color: '#6366f1',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px 32px',
      borderRadius: '30px',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 32px rgba(255, 255, 255, 0.3)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },

    // OFFER SECTION
    offerSection: {
      padding: isMobile ? '36px 8px' : (isTablet ? '65px 16px' : '80px 20px'),
      backgroundColor: '#f8fafc',
      position: 'relative',
      zIndex: 2
    },
    sectionContent: {
      maxWidth: isMobile ? '98vw' : '1200px',
      margin: '0 auto'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.5rem' : (isTablet ? '2rem' : '2.5rem'),
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: isMobile ? '8px' : '16px',
      color: '#1e293b'
    },
    sectionDescription: {
      fontSize: isMobile ? '0.98rem' : '1.1rem',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: isMobile ? '28px' : '60px',
      maxWidth: isMobile ? '98vw' : '600px',
      margin: isMobile ? '0 auto 28px' : '0 auto 60px'
    },
    offersGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(380px, 1fr))'),
      gap: isMobile ? '20px' : '32px'
    },
    offerCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '20px',
      padding: isMobile ? '20px' : '32px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
    },
    offerIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.4rem' : '1.7rem',
      marginBottom: '16px',
      backgroundColor: '#ede9fe',
      color: '#6366f1'
    },
    offerTitle: {
      fontSize: isMobile ? '1.07rem' : '1.3rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '10px'
    },
    offerDescription: {
      fontSize: isMobile ? '0.95rem' : '1rem',
      color: '#64748b',
      lineHeight: 1.6
    },

    // ADVANTAGES
    advantagesSection: {
      padding: isMobile ? '36px 8px' : (isTablet ? '65px 16px' : '80px 20px'),
      backgroundColor: '#fff',
      position: 'relative',
      zIndex: 2
    },
    advantagesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(250px, 1fr))'),
      gap: isMobile ? '12px' : '24px',
      marginTop: isMobile ? '20px' : '40px'
    },
    advantageItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: isMobile ? '11px' : '16px',
      backgroundColor: '#f1f5f9',
      borderRadius: '12px',
      transition: 'all 0.3s ease'
    },
    advantageIcon: {
      color: '#22c55e',
      fontSize: '20px'
    },
    advantageText: {
      fontSize: isMobile ? '0.94rem' : '1rem',
      color: '#475569',
      fontWeight: 500
    },

    // CONTACT
    contactSection: {
      padding: isMobile ? '36px 8px' : (isTablet ? '65px 16px' : '80px 20px'),
      backgroundColor: '#f8fafc',
      position: 'relative',
      zIndex: 2
    },
    formContainer: {
      maxWidth: isMobile ? '99vw' : '600px',
      margin: '0 auto'
    },
    formCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '24px',
      padding: isMobile ? '18px 8px' : '40px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
    },
    formTitle: {
      fontSize: isMobile ? '1.3rem' : '1.8rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: isMobile ? '19px' : '32px',
      textAlign: 'center'
    },
    formRow: {
      display: isMobile ? 'flex' : 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0px' : '20px',
      marginBottom: isMobile ? '0px' : '24px'
    },
    formGroup: {
      flex: 1,
      marginBottom: isMobile ? '20px' : '24px'
    },
    label: {
      display: 'block',
      fontSize: isMobile ? '0.96rem' : '0.99rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: isMobile ? '6px' : '8px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s ease',
      resize: 'vertical',
      minHeight: isMobile ? '80px' : '120px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    submitBtn: {
      width: '100%',
      padding: isMobile ? '13px' : '16px 24px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: isMobile ? '1.01rem' : '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.institutionName || !formData.contactPerson || !formData.email || !formData.institutionType || !formData.partnershipInterest) {
        alert('Please fill out all required fields.');
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New Institutional Partnership Inquiry
      -------------------------------------

      Institution Details:
      - Name: ${formData.institutionName}
      - Type: ${formData.institutionType}

      Contact Person:
      - Name: ${formData.contactPerson}
      - Email: ${formData.email}
      - Phone: ${formData.phoneNumber || 'Not provided'}

      Partnership Interest:
      - Area: ${formData.partnershipInterest}

      Message:
      ${formData.message || 'No message provided.'}
    `;

    const templateParams = {
        email_subject: `Partnership Inquiry from ${formData.institutionName}`,
        email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
        .then((response) => {
            console.log('SUCCESS!', response.status, response.text);
            alert('Thank you for your inquiry! Our partnerships team will get in touch with you shortly.');
            setIsSubmitting(false);
            setFormData({
                institutionName: '', contactPerson: '', email: '', phoneNumber: '',
                institutionType: '', partnershipInterest: '', message: ''
            });
        })
        .catch((err) => {
            console.error('FAILED...', err);
            alert('An error occurred while sending your inquiry. Please try again later.');
            setIsSubmitting(false);
        });
  };

  const handleInputFocus = (e) => {
    e.target.style.border = '2px solid #6366f1';
    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
  };
  const handleInputBlur = (e) => {
    e.target.style.border = '2px solid #e2e8f0';
    e.target.style.boxShadow = 'none';
  };
  const handleCardHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.1)';
  };
  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
  };
  const handleBtnHover = (e) => {
    const tgt = e.currentTarget;
    if (tgt.disabled) return;
    tgt.style.transform = 'translateY(-2px)';
    if (tgt.style.background.includes('ffffff')) {
      tgt.style.boxShadow = '0 12px 40px rgba(255, 255, 255, 0.4)';
    } else {
      tgt.style.boxShadow = '0 12px 40px rgba(99, 102, 241, 0.4)';
    }
  };
  const handleBtnLeave = (e) => {
    const tgt = e.currentTarget;
    tgt.style.transform = 'translateY(0)';
    if (tgt.style.background.includes('ffffff')) {
      tgt.style.boxShadow = '0 8px 32px rgba(255, 255, 255, 0.3)';
    } else {
      tgt.style.boxShadow = '0 8px 32px rgba(99, 102, 241, 0.3)';
    }
  };

  // Data
  const offers = [
    {
      icon: '🔬',
      title: 'AI Research Collaboration',
      description: 'Joint research projects, access to cutting-edge AI technologies, and collaborative innovation programs.'
    },
    {
      icon: '👥',
      title: 'Talent Development',
      description: 'Professional training programs, certification courses, and skill development initiatives for your workforce.'
    },
    {
      icon: '🔄',
      title: 'Technology Transfer',
      description: 'Seamless integration of AI solutions, technical support, and implementation guidance.'
    },
    {
      icon: '🛡️',
      title: 'Ethical AI Framework',
      description: 'Responsible AI development guidelines, governance frameworks, and compliance support.'
    }
  ];
  const advantages = [
    'Access to global AI research network',
    'Priority access to latest AI innovations',
    'Joint funding opportunities',
    'International collaboration platforms',
    'Custom AI solution development',
    'Regular knowledge exchange sessions',
    'Dedicated partnership management',
    'Co-branding and marketing opportunities'
  ];

  return (
    <div>
      <Navbar />
      <div style={styles.container}>
        {/* HERO */}
        <div style={styles.heroSection}>
          <div style={styles.heroContent}>
            <h1 style={styles.mainTitle}>
              Institutional <span style={styles.titleHighlight}>Partnerships</span>
            </h1>
            <p style={styles.heroDescription}>
              Join the Global AI Society network and shape the future of artificial intelligence together
            </p>
            <a
              href="#contact-form"
              style={styles.startButton}
              onMouseEnter={handleBtnHover}
              onMouseLeave={handleBtnLeave}
              onTouchStart={handleBtnHover}
              onTouchEnd={handleBtnLeave}
            >
              🚀 Start Partnership Journey
            </a>
          </div>
        </div>

        {/* OFFER */}
        <div style={styles.offerSection}>
          <div style={styles.sectionContent}>
            <h2 style={styles.sectionTitle}>
              What We <span style={{ color: '#6366f1' }}>Offer</span>
            </h2>
            <p style={styles.sectionDescription}>
              Comprehensive partnership solutions designed to accelerate your AI journey and maximize institutional impact
            </p>
            <div style={styles.offersGrid}>
              {offers.map((offer, index) => (
                <div
                  key={index}
                  style={styles.offerCard}
                  onMouseEnter={handleCardHover}
                  onMouseLeave={handleCardLeave}
                  onTouchStart={handleCardHover}
                  onTouchEnd={handleCardLeave}
                >
                  <div style={styles.offerIcon}>{offer.icon}</div>
                  <h3 style={styles.offerTitle}>{offer.title}</h3>
                  <p style={styles.offerDescription}>{offer.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ADVANTAGES */}
        <div style={styles.advantagesSection}>
          <div style={styles.sectionContent}>
            <h2 style={styles.sectionTitle}>
              Partnership <span style={{ color: '#6366f1' }}>Advantages</span>
            </h2>
            <p style={styles.sectionDescription}>
              Experience unparalleled benefits and exclusive opportunities as our institutional partner
            </p>
            <div style={styles.advantagesGrid}>
              {advantages.map((adv, idx) => (
                <div key={idx} style={styles.advantageItem}>
                  <span style={styles.advantageIcon}>✓</span>
                  <span style={styles.advantageText}>{adv}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* CONTACT */}
        <div id="contact-form" style={styles.contactSection}>
          <div style={styles.formContainer}>
            <h2 style={styles.sectionTitle}>
              Let's <span style={{ color: '#6366f1' }}>Connect</span>
            </h2>
            <p style={styles.sectionDescription}>
              Ready to explore partnership opportunities? Get in touch with our team to discuss how we can collaborate
            </p>
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Partnership Inquiry</h3>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Institution Name *</label>
                    <input
                      type="text" name="institutionName" value={formData.institutionName}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Contact Person *</label>
                    <input
                      type="text" name="contactPerson" value={formData.contactPerson}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input} required />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email Address *</label>
                    <input
                      type="email" name="email" value={formData.email}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone Number *</label>
                    <input
                      type="tel" name="phoneNumber" value={formData.phoneNumber}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input} required/>
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Institution Type *</label>
                    <select
                      name="institutionType" value={formData.institutionType}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.select} required>
                      <option value="">Select institution type</option>
                      <option value="university">University</option>
                      <option value="research">Research Institute</option>
                      <option value="corporate">Corporate</option>
                      <option value="government">Government</option>
                      <option value="non-profit">Non-Profit</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Partnership Interest *</label>
                    <select
                      name="partnershipInterest" value={formData.partnershipInterest}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.select} required>
                      <option value="">Select area of interest</option>
                      <option value="research">Research Collaboration</option>
                      <option value="training">Training Programs</option>
                      <option value="technology">Technology Transfer</option>
                      <option value="ethics">Ethical AI Framework</option>
                    </select>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Message</label>
                  <textarea
                    name="message" value={formData.message}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={styles.textarea}
                    placeholder="Tell us about your partnership goals and how we can work together..."
                  />
                </div>
                <button
                  type="submit"
                  style={styles.submitBtn}
                  onMouseEnter={handleBtnHover}
                  onMouseLeave={handleBtnLeave}
                  onTouchStart={handleBtnHover}
                  onTouchEnd={handleBtnLeave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : '🚀 Submit Partnership Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default InstitutionalPartnerships;
